import React, { createContext, useState, useCallback, useContext, useEffect } from 'react';
import { ROLES } from '../constants/roles';

const ClientAuthContext = createContext();

const CLIENT_SESSION_KEY = 'satellite_client_session';

export const useClientAuth = () => {
  const context = useContext(ClientAuthContext);
  if (!context) {
    throw new Error('useClientAuth must be used within a ClientAuthProvider');
  }
  return context;
};

export const ClientAuthProvider = ({ children }) => {
  const [clientUser, setClientUser] = useState(null);
  const [isClientAuthenticated, setIsClientAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize from stored session
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CLIENT_SESSION_KEY);
      if (stored) {
        const session = JSON.parse(stored);
        // Check if session is still valid
        if (session.expiresAt && Date.now() < session.expiresAt) {
          setClientUser(session);
          setIsClientAuthenticated(true);
        } else {
          localStorage.removeItem(CLIENT_SESSION_KEY);
        }
      }
    } catch (err) {
      console.error('Failed to restore client session:', err);
      localStorage.removeItem(CLIENT_SESSION_KEY);
    }
    setIsLoading(false);
  }, []);

  // Verify a magic link token via the Netlify function
  const verifyToken = useCallback(async (token) => {
    setIsLoading(true);
    setError(null);

    try {
      const isProduction = process.env.NODE_ENV === 'production';
      const apiUrl = isProduction
        ? '/.netlify/functions/magic-link'
        : 'http://localhost:3001/api/magic-link';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'verify', token })
      });

      const result = await response.json();

      if (!result.success) {
        setError(result.error || 'Invalid or expired link');
        setIsLoading(false);
        return false;
      }

      const clientSession = {
        email: result.data.email,
        clientName: result.data.clientName,
        role: 'client',
        permissions: result.data.permissions || ROLES.CLIENT.permissions,
        expiresAt: result.data.expiresAt
      };

      setClientUser(clientSession);
      setIsClientAuthenticated(true);
      localStorage.setItem(CLIENT_SESSION_KEY, JSON.stringify(clientSession));
      setIsLoading(false);
      return true;
    } catch (err) {
      console.error('Token verification failed:', err);
      setError('Unable to verify your link. Please try again or request a new one.');
      setIsLoading(false);
      return false;
    }
  }, []);

  // Logout client
  const clientLogout = useCallback(() => {
    setClientUser(null);
    setIsClientAuthenticated(false);
    setError(null);
    localStorage.removeItem(CLIENT_SESSION_KEY);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const contextValue = {
    clientUser,
    isClientAuthenticated,
    isLoading,
    error,
    verifyToken,
    clientLogout,
    clearError,
    clientName: clientUser?.clientName || '',
    clientEmail: clientUser?.email || ''
  };

  return (
    <ClientAuthContext.Provider value={contextValue}>
      {children}
    </ClientAuthContext.Provider>
  );
};

export default ClientAuthContext;
